import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SalesInvoicesWrapper } from "./styled";
import {
  Box,
  ButtonStyledIconLeft,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import {
  PlusIcon,
  SalesInvoicesIconWithBg,
  SearchIcon,
} from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import { InvoicesListColumns } from "../../../helpers/adminConstants/TableConstants";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import SalesInvoiceApi from "../../../services/api/salesInvoiceApi";
import { useDebounce } from "../../../hooks/useDebounce";
import { toast } from "react-toastify";
import { useUser } from "../../../context/userContext";
import { generateInvoicePDF } from "../../../utils/generationPdf";
import { getModuleOperations, toBase64 } from "../../../utils";

const SalesInvoices = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [invoicesList, setInvoicesList] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [totalCount, setTotalCount] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [logoBase64, setLogoBase64] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (user?.organization?.logo) {
      toBase64(
        `${process.env.REACT_APP_ASSET_URL}/${user?.organization?.logo}`
      ).then(setLogoBase64);
    }
  }, []);

  const onDelete = (row) => {
    setInvoiceId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (invoice) => {
    navigate(`/sales-invoices/${invoice?.id}/view-invoice`);
  };

  const handleEdit = (invoice) => {
    navigate(`/sales-invoices/${invoice?.id}/edit-invoice`);
  };

  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await SalesInvoiceApi.deleteInvoice(invoiceId);
      if (response.code === 200) {
        toast.success(t("invoice_deleted_successfully"));
        setShowDeleteModal(false);
        fetchInvoicesList(activePage);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleDownload = async (row) => {
    const data = await fetchInvoiceDetails(row?.id);
    await generateInvoicePDF(data, logoBase64, "sales_invoice.pdf");
  };

  const handleAdd = async () => {
    try {
      setCheckingLimit(true);
      const response = await SalesInvoiceApi.checkInvoiceLimit();
      if (response?.data?.can_create) navigate("/sales-invoices/create-invoice");
      else if (response?.message) toast.error(response?.message);
    } catch (error) {
    } finally {
      setCheckingLimit(false);
    }
  };
  const fetchInvoiceDetails = async (id) => {
    try {
      const { data, code } = await SalesInvoiceApi.viewInvoice(id);
      if (code === 200 && data) {
        // setDataObj(data);
        return data;
      }
    } catch (error) {
      toast.error(error?.message);
      return null;
    } finally {
    }
  };

  const fetchInvoicesList = async (page, query) => {
    try {
      setLoading(true);
      const { data, code } = await SalesInvoiceApi.fetchSalesInvoiceList(
        page,
        query
      );
      if (code === 200) {
        setInvoicesList(data?.data);
        setTotalCount(data?.meta?.total);
        setPerPage(data?.meta?.per_page);
      } else {
        setInvoicesList([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoicesList(activePage, debouncedSearchQuery);
  }, [language, activePage, debouncedSearchQuery]);

  return (
    <SalesInvoicesWrapper>
      <Box className="content-head">
        <Box className="w-fit d-flex align-items-center gap-3">
          <SalesInvoicesIconWithBg />
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("sales_invoices")}
          </SubHeadingStyled>
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            icon={SearchIcon}
            onChange={handleSearch}
          />

          {getModuleOperations(5, 1) && (
            <ButtonStyledIconLeft
              label={t("create_invoice")}
              icon={PlusIcon}
              isLoading={checkingLimit}
              disabled={checkingLimit}
              onClick={handleAdd}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            typeId={5}
            options={["view", "download", "delete"]}
            rows={invoicesList}
            columns={InvoicesListColumns}
            onDelete={onDelete}
            onOpen={handleView}
            onEdit={handleEdit}
            onDownload={handleDownload}
            // onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={totalCount}
            perPage={perPage}
          />
        )}
      </Box>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("invoice")}
        isLoading={loadingDelete}
      />
    </SalesInvoicesWrapper>
  );
};

export default SalesInvoices;
